import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  CreditCard,
  Settings,
  Wrench,
  Tags,
  Radio,
  Cable,
  ChevronDown,
} from "lucide-react";
import logoLyrax from "../../../assets/images/logo-lyrax.png";
import logoLyraxWhite from "../../../assets/images/logo-lyrax-white.png";

interface MenuChild {
  label: string;
  path: string;
  icon?: React.ElementType;
}

interface MenuItem {
  label: string;
  icon: React.ElementType;
  path?: string;
  section?: string;
  children?: MenuChild[];
}

const menuItems: MenuItem[] = [
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    path: "/dashboard",
    section: "Accesos rápidos",
  },
  {
    label: "Tenants",
    icon: Building2,
    path: "/tenants",
    section: "Gestión",
  },
  {
    label: "Planes",
    icon: CreditCard,
    path: "/plans",
  },
  {
    label: "Mantenedores",
    icon: Wrench,
    children: [
      { label: "Estados homologados", path: "/mantenedores/estados", icon: Tags },
      { label: "Canales", path: "/mantenedores/canales", icon: Radio },
      {
        label: "Endpoints de integración",
        path: "/mantenedores/endpoints",
        icon: Cable,
      },
    ],
  },
  {
    label: "Configuración",
    icon: Settings,
    path: "/settings",
    section: "Sistema",
  },
];

type SidebarProps = { collapsed: boolean };

type FlyoutState = {
  label: string;
  top: number;
  left: number;
} | null;

export const Sidebar: React.FC<SidebarProps> = ({ collapsed }) => {
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    Mantenedores: location.pathname.startsWith("/mantenedores"),
  });
  const [flyout, setFlyout] = useState<FlyoutState>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setFlyout(null), 150);
  };

  useEffect(() => {
    if (!collapsed) setFlyout(null);
  }, [collapsed]);

  useEffect(() => () => clearCloseTimer(), []);

  useEffect(() => {
    if (!flyout) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const groupEl = groupRefs.current[flyout.label];
      if (
        flyoutRef.current?.contains(target) ||
        groupEl?.contains(target)
      ) {
        return;
      }
      setFlyout(null);
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [flyout]);

  const openFlyout = (label: string) => {
    clearCloseTimer();
    const el = groupRefs.current[label];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setFlyout({
      label,
      top: rect.top,
      left: rect.right + 8,
    });
  };

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const linkBase =
    "flex items-center gap-3 px-3 py-2.5 rounded-lyrax text-sm font-medium transition-all duration-200";

  const activeChildren = menuItems.find((m) => m.label === flyout?.label)?.children;

  return (
    <aside
      className={`${
        collapsed ? "w-[4.5rem]" : "w-64"
      } bg-lyrax-sidebar flex flex-col transition-all duration-300 h-full shrink-0 relative overflow-hidden`}
    >
      <div className="pointer-events-none absolute inset-0 admin-grain opacity-40" />

      <div className={`relative z-10 h-20 flex items-center ${collapsed ? "justify-center px-2" : "px-5 gap-3"}`}>
        {collapsed ? (
          <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-lyrax-primary text-lyrax-primary-ink font-bold text-lg ">
            L
          </div>
        ) : (
          <>
            <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-lyrax-primary text-lyrax-primary-ink font-bold shrink-0">
              L
            </div>
            <div className="min-w-0">
              <p className="text-lyrax-sidebar-text font-bold tracking-[0.12em] text-sm uppercase leading-none">
                Lyrax
              </p>
              <p className="text-[10px] text-lyrax-sidebar-muted mt-1 tracking-wide uppercase">
                Super Admin
              </p>
            </div>
          </>
        )}
      </div>

      <nav className="relative z-10 flex-1 px-3 py-2 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isGroupActive = item.children?.some((c) =>
            location.pathname.startsWith(c.path),
          );

          return (
            <div key={item.label}>
              {item.section && !collapsed && (
                <p className="px-3 pt-4 pb-2 text-xs font-semibold uppercase tracking-[0.14em] text-lyrax-sidebar-section">
                  {item.section}
                </p>
              )}

              {item.children?.length ? (
                <div
                  className="space-y-1"
                  ref={(node) => {
                    groupRefs.current[item.label] = node;
                  }}
                  onMouseEnter={() => {
                    if (collapsed) openFlyout(item.label);
                  }}
                  onMouseLeave={() => {
                    if (collapsed) scheduleClose();
                  }}
                >
                  <button
                    type="button"
                    title={item.label}
                    onClick={() => {
                      if (collapsed) {
                        if (flyout?.label === item.label) {
                          setFlyout(null);
                        } else {
                          openFlyout(item.label);
                        }
                      } else {
                        toggleGroup(item.label);
                      }
                    }}
                    className={`w-full ${collapsed ? "justify-center px-0" : ""} ${linkBase} ${
                      isGroupActive
                        ? "bg-lyrax-primary text-lyrax-primary-ink "
                        : "text-lyrax-sidebar-text/80 hover:bg-lyrax-sidebar-hover hover:text-lyrax-sidebar-text"
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${
                            openGroups[item.label] || isGroupActive ? "rotate-180" : ""
                          }`}
                        />
                      </>
                    )}
                  </button>

                  {!collapsed && (openGroups[item.label] || isGroupActive) && (
                    <div className="ml-4 pl-3 space-y-0.5 border-l border-lyrax-sidebar-line">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon || Tags;
                        return (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            className={({ isActive }) =>
                              `flex items-center gap-2 px-3 py-2 rounded-lyrax text-sm transition-all duration-200 ${
                                isActive
                                  ? "bg-lyrax-primary/15 text-lyrax-sidebar-section"
                                  : "text-lyrax-sidebar-muted hover:text-lyrax-sidebar-text hover:bg-lyrax-sidebar-hover"
                              }`
                            }
                          >
                            <ChildIcon className="w-4 h-4 flex-shrink-0" />
                            <span>{child.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  to={item.path || "#"}
                  title={collapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    `${linkBase} ${collapsed ? "justify-center px-0" : ""} ${
                      isActive
                        ? "bg-lyrax-primary text-lyrax-primary-ink "
                        : "text-lyrax-sidebar-text/80 hover:bg-lyrax-sidebar-hover hover:text-lyrax-sidebar-text"
                    }`
                  }
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              )}
            </div>
          );
        })}
      </nav>

      <div className="relative z-10 p-4 border-t border-lyrax-sidebar-line dark:border-transparent">
        {collapsed ? (
          <div className="flex justify-center opacity-50">
            <img src={logoLyrax} alt="Lyrax" className="h-5 object-contain dark:hidden" />
            <img src={logoLyraxWhite} alt="Lyrax" className="h-5 object-contain hidden dark:block" />
          </div>
        ) : (
          <div className="flex items-center justify-center gap-1.5 text-[11px] text-lyrax-sidebar-muted">
            <span>Powered by</span>
            <img src={logoLyrax} alt="Lyrax" className="h-4 object-contain dark:hidden" />
            <img src={logoLyraxWhite} alt="Lyrax" className="h-4 object-contain opacity-80 hidden dark:block" />
          </div>
        )}
      </div>

      {collapsed &&
        flyout &&
        activeChildren &&
        createPortal(
          <div
            ref={flyoutRef}
            className="fixed z-[100] min-w-[13rem] rounded-lyrax bg-lyrax-sidebar border border-lyrax-sidebar-line shadow-lyrax py-2 px-1.5 animate-fade-up"
            style={{ top: flyout.top, left: flyout.left }}
            onMouseEnter={() => openFlyout(flyout.label)}
            onMouseLeave={scheduleClose}
          >
            <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-lyrax-sidebar-section">
              {flyout.label}
            </p>
            {activeChildren.map((child) => {
              const ChildIcon = child.icon || Tags;
              return (
                <NavLink
                  key={child.path}
                  to={child.path}
                  onClick={() => setFlyout(null)}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-lyrax text-sm transition-all duration-200 ${
                      isActive
                        ? "bg-lyrax-primary text-lyrax-primary-ink"
                        : "text-lyrax-sidebar-muted hover:text-lyrax-sidebar-text hover:bg-lyrax-sidebar-hover"
                    }`
                  }
                >
                  <ChildIcon className="w-4 h-4 flex-shrink-0" />
                  <span>{child.label}</span>
                </NavLink>
              );
            })}
          </div>,
          document.body,
        )}
    </aside>
  );
};
